import { useEffect, useState } from "react";
import { Check, MessageCircle, PhoneOff, SkipForward, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Sequenza guidata: WhatsApp non consente invii in blocco, quindi si va uno alla volta. */
export const WhatsAppSequence = ({ leads, onClose, onMark }) => {
  const [index, setIndex] = useState(0);
  const [message, setMessage] = useState("");
  const [done, setDone] = useState(0);

  const lead = leads[index];

  useEffect(() => {
    setMessage(lead?.messaggio_whatsapp || "");
  }, [lead]);

  if (!lead) {
    return (
      <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 p-4">
        <div
          className="w-full max-w-md rounded-2xl bg-white p-8 text-center"
          data-testid="wa-seq-finished"
        >
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-100">
            <Check className="h-7 w-7 text-emerald-600" />
          </div>
          <h2 className="mt-4 text-base font-bold md:text-lg">Sequenza finita</h2>
          <p className="mt-2 text-sm text-slate-500">
            {done} messaggi segnati come inviati. Fra tre giorni li ritrovi nella lista dei
            richiami, se non rispondono.
          </p>
          <Button
            data-testid="wa-seq-close"
            onClick={onClose}
            className="mt-6 w-full bg-slate-900 text-white hover:bg-slate-800"
          >
            Chiudi
          </Button>
        </div>
      </div>
    );
  }

  const phone = (lead.telefono || "").replace(/\D/g, "").replace(/^39/, "");
  const waHref = `https://wa.me/39${phone}?text=${encodeURIComponent(message)}`;

  const advance = async (status) => {
    if (status) await onMark(lead, status);
    if (status === "inviato") setDone((d) => d + 1);
    setIndex((i) => i + 1);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 p-4">
      <div
        className="flex w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white"
        data-testid="wa-sequence"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Messaggio {index + 1} di {leads.length}
            </p>
            <h2 className="text-base font-bold md:text-lg" data-testid="wa-seq-name">
              {lead.nome_azienda}
            </h2>
          </div>
          <button
            type="button"
            data-testid="wa-seq-x"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="font-mono-num font-semibold text-slate-700">{lead.telefono}</span>
            <span>{lead.citta || lead.regione}</span>
            <span>{lead.categoria_label}</span>
            {lead.sample_token ? (
              <span className="font-semibold text-emerald-700">preventivo esca incluso</span>
            ) : (
              <span className="font-semibold text-amber-600">nessuna esca preparata</span>
            )}
          </div>

          <textarea
            data-testid="wa-seq-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={7}
            className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <p className="text-xs text-slate-400">
            Puoi modificarlo prima di inviare: un messaggio leggermente diverso ogni volta riduce
            il rischio di essere segnalata come spam.
          </p>

          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            data-testid="wa-seq-open"
            onClick={() => advance("inviato")}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3.5 text-sm font-bold text-white transition-colors hover:bg-emerald-500"
          >
            <MessageCircle className="h-4 w-4" /> Apri WhatsApp e passa al prossimo
          </a>

          <div className="flex gap-2">
            <Button
              data-testid="wa-seq-skip"
              variant="outline"
              onClick={() => advance(null)}
              className="flex-1 gap-2"
            >
              <SkipForward className="h-4 w-4" /> Salta
            </Button>
            <Button
              data-testid="wa-seq-nowa"
              variant="outline"
              onClick={() => advance("no_whatsapp")}
              className="flex-1 gap-2 text-slate-600"
            >
              <PhoneOff className="h-4 w-4" /> Niente WhatsApp
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSequence;
